import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { COLORS } from '../lib/styleGuide';
import { 
  getStoreReviews, 
  getStoreRatingSummary, 
  getStoreProducts, 
  formatReviewDate,
  generateStarRating 
} from '../lib/reviewsService';
import { 
  Star,
  Search,
  ArrowDown
} from 'react-iconly';
import { BackButton } from '../components/ui';

// Typography constants
const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 400
};

const buttonFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 600
};

export function StoreReviews() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [reviews, setReviews] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter and sort state
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Fetch initial data
  useEffect(() => {
    if (storeId) {
      fetchInitialData();
    }
  }, [storeId]);

  // Fetch reviews when filters change
  useEffect(() => {
    if (storeId && ratingSummary) {
      fetchReviews(true); // Reset pagination
    }
  }, [selectedProduct, sortBy, sortOrder, searchTerm]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch rating summary and products in parallel
      const [summaryResult, productsResult] = await Promise.all([
        getStoreRatingSummary(storeId),
        getStoreProducts(storeId)
      ]);

      // getStoreRatingSummary returns data directly, not wrapped in success/data
      console.log('🔍 StoreReviews - Raw summary result:', summaryResult);
      setRatingSummary(summaryResult);

      // getStoreProducts also returns data directly
      console.log('🔍 StoreReviews - Raw products result:', productsResult);
      setProducts(productsResult);

      // Fetch initial reviews
      await fetchReviews(true);

    } catch (err) {
      setError(err.message || 'Failed to load review data');
      console.error('Error fetching initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (reset = false) => {
    try {
      console.log('🔍 StoreReviews - fetchReviews called with reset:', reset);
      
      // getStoreReviews returns an array directly, not wrapped in success/data
      const reviews = await getStoreReviews(storeId, 50); // Get up to 50 reviews
      
      console.log('🔍 StoreReviews - Raw reviews result:', reviews);
      console.log('🔍 StoreReviews - Review details:', reviews.map(r => ({ 
        id: r.id, 
        customerName: r.customerName, 
        comment: r.comment, 
        rating: r.rating,
        productName: r.productName 
      })));
      
      // Filter reviews based on selected product
      let filteredReviews = reviews;
      if (selectedProduct !== 'all') {
        filteredReviews = reviews.filter(review => review.productId === selectedProduct);
      }
      
      // Apply search filter
      if (searchTerm) {
        filteredReviews = filteredReviews.filter(review => 
          review.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          review.productName?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Apply sorting
      filteredReviews.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
        
        if (sortBy === 'rating') {
          return sortOrder === 'desc' ? b.rating - a.rating : a.rating - b.rating;
        } else {
          return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
        }
      });
      
      // Simple pagination - just set all reviews for now
      setReviews(filteredReviews);
      setCurrentPage(1);
      setHasMore(false); // No pagination for now
      
    } catch (err) {
      setError(err.message || 'Failed to load reviews');
      console.error('Error fetching reviews:', err);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
      fetchReviews(false);
    }
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle order if same sort field
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(newSortBy);
      setSortOrder(newSortBy === 'date' ? 'desc' : 'desc'); // Default to desc for both
    }
  };

  // Render star rating
  const renderStarRating = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          set={i <= rating ? 'bold' : 'light'}
          size={16}
          primaryColor={i <= rating ? COLORS.WARNING_ORANGE : COLORS.NEUTRAL_GRAY}
        />
      );
    }
    return <div style={{ display: 'flex', gap: 2 }}>{stars}</div>;
  };

  // Render rating breakdown bar
  const renderRatingBar = (starCount, count, total) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 12,
        marginBottom: 8
      }}>
        <div style={{ 
          fontSize: 14, 
          color: COLORS.NEUTRAL_TEXT,
          minWidth: 60,
          ...bodyFont
        }}>
          {starCount} star{starCount !== 1 ? 's' : ''}
        </div>
        <div style={{ 
          flex: 1, 
          height: 8, 
          background: COLORS.NEUTRAL_GRAY,
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          <div style={{ 
            width: `${percentage}%`, 
            height: '100%', 
            background: COLORS.PRIMARY_GRADIENT,
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ 
          fontSize: 14, 
          color: COLORS.NEUTRAL_TEXT,
          minWidth: 20,
          textAlign: 'right',
          ...bodyFont
        }}>
          {count}
        </div>
      </div>
    );
  };

  if (loading && !ratingSummary) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        padding: '20px'
      }}>
        <div style={{ 
          maxWidth: 800, 
          margin: '0 auto',
          padding: '20px'
        }}>
          <div style={{ 
            background: COLORS.NEUTRAL_WHITE, 
            borderRadius: 16, 
            padding: 40,
            textAlign: 'center',
            color: COLORS.ACCENT_GRAY
          }}>
            <div style={{ fontSize: 18, ...bodyFont }}>Loading reviews...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        padding: '20px'
      }}>
        <div style={{ 
          maxWidth: 800, 
          margin: '0 auto',
          padding: '20px'
        }}>
          <div style={{ 
            background: COLORS.NEUTRAL_WHITE, 
            borderRadius: 16, 
            padding: 40,
            textAlign: 'center',
            color: COLORS.ERROR_RED
          }}>
            <div style={{ fontSize: 18, ...bodyFont, marginBottom: 16 }}>Error loading reviews</div>
            <div style={{ fontSize: 14, ...bodyFont, color: COLORS.ACCENT_GRAY }}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: COLORS.NEUTRAL_BG,
      padding: '20px'
    }}>
      <div style={{ 
        maxWidth: 800, 
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: 24
        }}>
          <BackButton
            onClick={() => navigate(-1)}
            variant="standard"
            ariaLabel="Back"
          />
        </div>

        {/* Rating Summary Card */}
        {ratingSummary && (
          <div style={{ 
            background: COLORS.NEUTRAL_WHITE, 
            borderRadius: 16, 
            padding: 24,
            marginBottom: 24,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: 40,
              alignItems: 'flex-start'
            }}>
              {/* Overall Rating */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: 48, 
                  fontWeight: 700, 
                  background: COLORS.PRIMARY_GRADIENT,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: 8,
                  ...headingFont
                }}>
                  {(ratingSummary.avgRating || 0).toFixed(1)}
                </div>
                {renderStarRating(ratingSummary.avgRating || 0)}
                <div style={{ 
                  fontSize: 14, 
                  color: COLORS.ACCENT_GRAY,
                  marginTop: 8,
                  ...bodyFont
                }}>
                  ({ratingSummary.reviewCount || 0} review{(ratingSummary.reviewCount || 0) !== 1 ? 's' : ''})
                </div>
              </div>

              {/* Rating Breakdown */}
              <div style={{ flex: 1 }}>
                {[5, 4, 3, 2, 1].map(starCount => 
                  <div key={starCount}>
                    {renderRatingBar(
                      starCount, 
                      (ratingSummary.ratingBreakdown && ratingSummary.ratingBreakdown[starCount]) || 0, 
                      ratingSummary.reviewCount || 0
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div style={{ 
          background: COLORS.NEUTRAL_WHITE, 
          borderRadius: 16, 
          padding: 24,
          marginBottom: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            alignItems: 'end'
          }}>
            {/* Filter by Product */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 12, 
                fontWeight: 600, 
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: 8,
                ...bodyFont
              }}>
                Filter by product
              </label>
                              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${COLORS.NEUTRAL_GRAY}`,
                  borderRadius: 8,
                  fontSize: 12,
                  background: COLORS.NEUTRAL_WHITE,
                  cursor: 'pointer',
                  ...bodyFont
                }}
              >
                <option value="all">All products</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 12, 
                fontWeight: 600, 
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: 8,
                ...bodyFont
              }}>
                Sort by
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleSortChange('date')}
                  style={{
                    background: sortBy === 'date' ? COLORS.PRIMARY_GRADIENT : COLORS.NEUTRAL_WHITE,
                    color: sortBy === 'date' ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                    border: `2px solid ${sortBy === 'date' ? 'transparent' : COLORS.NEUTRAL_GRAY}`,
                    borderRadius: 8,
                    padding: '10px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    ...buttonFont
                  }}
                >
                  Date {sortBy === 'date' && (sortOrder === 'desc' ? '↓' : '↑')}
                </button>
                <button
                  onClick={() => handleSortChange('rating')}
                  style={{
                    background: sortBy === 'rating' ? COLORS.PRIMARY_GRADIENT : COLORS.NEUTRAL_WHITE,
                    color: sortBy === 'rating' ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                    border: `2px solid ${sortBy === 'rating' ? 'transparent' : COLORS.NEUTRAL_GRAY}`,
                    borderRadius: 8,
                    padding: '10px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    ...buttonFont
                  }}
                >
                  Rating {sortBy === 'rating' && (sortOrder === 'desc' ? '↓' : '↑')}
                </button>
              </div>
            </div>

            {/* Search Reviews */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: 12, 
                fontWeight: 600, 
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: 8,
                ...bodyFont
              }}>
                Search reviews
              </label>
              <div style={{ 
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Search 
                  set="light" 
                  size={16} 
                  primaryColor={COLORS.ACCENT_GRAY}
                  style={{ 
                    position: 'absolute', 
                    left: 12, 
                    zIndex: 1 
                  }}
                />
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 40px',
                    border: `2px solid ${COLORS.NEUTRAL_GRAY}`,
                    borderRadius: 8,
                    fontSize: 12,
                    outline: 'none',
                    ...bodyFont
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div style={{ 
          background: COLORS.NEUTRAL_WHITE, 
          borderRadius: 16, 
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {reviews.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 40, 
              color: COLORS.ACCENT_GRAY,
              fontSize: 12,
              ...bodyFont
            }}>
              {searchTerm || selectedProduct !== 'all' 
                ? 'No reviews match your filters' 
                : 'No reviews yet'
              }
            </div>
          ) : (
            <>
              {reviews.map((review, index) => (
                <div 
                  key={review.id || index}
                  style={{ 
                    borderBottom: index < reviews.length - 1 ? `1px solid ${COLORS.NEUTRAL_GRAY}` : 'none',
                    paddingBottom: index < reviews.length - 1 ? 24 : 0,
                    marginBottom: index < reviews.length - 1 ? 24 : 0
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: 12
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {renderStarRating(review.rating || 0)}
                      {review.productName && (
                        <span style={{
                          background: COLORS.NEUTRAL_BG,
                          color: COLORS.NEUTRAL_TEXT,
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          ...buttonFont
                        }}>
                          {review.productName}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: 14, 
                      color: COLORS.ACCENT_GRAY,
                      ...bodyFont
                    }}>
                      {formatReviewDate(review.createdAt)}
                    </div>
                  </div>
                  
                  <div style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: COLORS.NEUTRAL_TEXT,
                    marginBottom: 8,
                    ...bodyFont
                  }}>
                    by {review.customerName || 'Anonymous'}
                  </div>
                  
                  <div style={{ 
                    fontSize: 14, 
                    color: COLORS.NEUTRAL_TEXT,
                    lineHeight: 1.5,
                    ...bodyFont
                  }}>
                    {review.comment || 'No review text provided.'}
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    style={{
                      background: COLORS.PRIMARY_GRADIENT,
                      color: COLORS.NEUTRAL_WHITE,
                      border: 'none',
                      borderRadius: 10,
                      padding: '12px 24px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1,
                      ...buttonFont
                    }}
                  >
                    {loading ? 'Loading...' : 'Load More Reviews'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
